import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  skipToken,
  hashKey,
} from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";
import { pick } from "ramda";
import { AxiosProgressEvent } from "axios";

import { api, ApiResponse, groupById } from "_/utils";
import { Tuple, Uuid } from "_/types";

import { BlobRecord, fetchBlobContentsParallel } from "_/data/blob";
import { useMaterials } from "_/data/materials";
import { type Machine } from "_/data/machines";

const SCOPE = "projects";
const KEYS = {
  all: () => [SCOPE],
  project: (id: Uuid | undefined) => [SCOPE, "detail", id],
  revision: (projectId: Uuid | undefined, revisionId: Uuid | undefined) => [
    ...KEYS.project(projectId),
    "revision",
    revisionId,
  ],
  revisionResources: (
    projectId: Uuid | undefined,
    revisionId: Uuid | undefined
  ) => [...KEYS.revision(projectId, revisionId), "resources"],
  optimizationCreatedRevisions: (
    projectId: Uuid | undefined,
    optimizationId: Uuid | undefined
  ) => [
    ...KEYS.projectOptimizations(projectId),
    "createdRevisions",
    optimizationId,
  ],
  allAttempts: () => [SCOPE, "attempts"],
  projectAttempts: (projectId: Uuid) => [...KEYS.allAttempts(), projectId],
  attempt: (projectId: Uuid, attemptId: Uuid | undefined) => [
    ...KEYS.projectAttempts(projectId),
    attemptId,
  ],
  // We deliberately cache polled attempts separately from regular attempts, so
  // that we don't trigger reloading all attempt datasets when polling for
  // attempt completion.
  polledAttempt: (projectId: Uuid, attemptId: Uuid | undefined) => {
    return [SCOPE, "polledAttempts", projectId, attemptId];
  },
  attemptData: (projectId: Uuid, attemptId: Uuid, datasetId: Uuid) => [
    ...KEYS.attempt(projectId, attemptId),
    datasetId,
  ],
  allOptimizations: () => [SCOPE, "optimizations"],
  projectOptimizations: (projectId: Uuid | undefined) => [
    ...KEYS.allOptimizations(),
    projectId,
  ],
};

export type ProjectServerResponseData = {
  meta: Omit<Project, "revisions">;
  revisions: Revision[];
};

function transformResponse(data: ProjectServerResponseData): Project {
  return { ...data.meta, revisions: data.revisions };
}

export type GcodeFile = {
  id: Uuid;
  file: BlobRecord;
  name: string;
  ownerId: Uuid;
  domainId: Uuid;
  createdAt: string;
  updatedAt: string;
  previewId?: Uuid | null;
};

export type Revision = {
  id: Uuid;
  projectId: Uuid;
  creatorId: Uuid;
  materials: Tuple<string | null>;
  createdAt: string;
  updatedAt: string;
  number: number;
  instructions: string | null;
  gcodeFile?: GcodeFile | null;
};

export type Project = {
  id: Uuid;
  domainId: Uuid;
  ownerId: Uuid;
  name: string;
  description: string;
  status: "active" | "archived" | "failed" | "complete" | "hold";
  revisions: Revision[];
  updatedAt: string;
  createdAt: string;
};

export type AttemptStatus =
  | "complete"
  | "cancelled"
  | "failed"
  | "printing"
  | "paused";

/**
 * Whether an attempt has finished, however it ended.
 *
 * Telemetry records are only bundled into a queryable file once an attempt
 * ends, so the server refuses to serve one before then. The map covers every
 * status, so adding one is a type error here.
 */
const ENDED_BY_STATUS: Record<AttemptStatus, boolean> = {
  complete: true,
  cancelled: true,
  failed: true,
  printing: false,
  paused: false,
};

export function hasAttemptEnded(status: AttemptStatus): boolean {
  return ENDED_BY_STATUS[status];
}

export type Attempt = {
  id: Uuid;
  machineId: Uuid;
  revisionId: Uuid;
  operatorId?: Uuid;
  fileName: string;
  startedAt: string;
  endedAt?: string;
  motion_data?: BlobRecord;
  status: AttemptStatus;
  timelapseId?: Uuid;
  // `number` is not present in the API response, but is added by the frontend
  // We'll probably want to add it to the server at some point.
  number: number;
};

export type AttemptDatasetManifest = {
  kind: string;
  samples: number;
  id: Uuid;
};

export interface RevisionMaterial {
  material1Id: Uuid | null;
  material2Id: Uuid | null;
}

type Resource = File | BlobRecord;

export interface RevisionChange {
  materials?: RevisionMaterial;
  gcode?: Resource;
  models?: Resource[];
}

export type JobStatus =
  | "queued"
  | "pending"
  | "started"
  | "completed"
  | "failed"
  | "cancelled";

export type Job<I, O> = {
  id: Uuid;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
  status: JobStatus;
  input: I;
  output: O;
  task: string;
  taskVersion: string;
  progress?: number;
};

export type StepStatus =
  | "pending"
  | "skipped"
  | "running"
  | "completed"
  | "failed"
  | "warning";

export type OptimizationStep = {
  step: string;
  status: StepStatus;
  progress: number;
  data: { error?: string; warning?: string; [key: string]: unknown } | null;
};

type SimJobInput = {
  parentOptimizationId?: Uuid;
  simulationRole?: "original" | "optimized";
};

export type Optimization = {
  job: Job<
    SimJobInput,
    { status: string; errors?: ([number, string] | string)[] } | undefined
  >;
  analysisOutput: BlobRecord | null;
  optimizedGcode: BlobRecord | null;
  optimizedOutput: BlobRecord | null;
  revisionId: Uuid;
  steps: OptimizationStep[];
  number: number;
};

export type OptimizationType = "analyze" | "analyze+optimize";

// Data payload for the OPTIMIZE_THERMALS step, as sent by BOE.
export type ThermalStepData = {
  optimization_strategy?:
    | "CooldownTower"
    | "SlowDownExtrusionsAndLowerTemperature";
};

// Settings for an optimization job provided by the user
export type SimJobSettings = {
  email: string;
  gcodeDownloadEndpoint: string;
  getReturnUrl: (jobId: Uuid) => string;
  description: string;
  jobType: OptimizationType;
  nozzle0MaterialId: Uuid;
  nozzle1MaterialId?: Uuid;
  plateMaterialId: Uuid;
  nozzle0MaterialName: string;
  nozzle1MaterialName?: string;
  plateMaterialName: string;
};

export function useProjects({ enabled = true }: { enabled?: boolean } = {}) {
  async function getProjects(): Promise<Project[]> {
    const response =
      await api.get<ApiResponse<ProjectServerResponseData[]>>("/projects");
    const data = response.data.data;

    return data.map(transformResponse);
  }

  const config: UseQueryOptions<Project[]> = {
    queryKey: KEYS.all(),
    queryFn: getProjects,
    enabled,
  };

  return useQuery(config);
}

export function useProject(id: Uuid) {
  async function getProject(): Promise<Project> {
    const response = await api.get<ApiResponse<ProjectServerResponseData>>(
      `/projects/${id}`
    );
    const data = response.data.data;

    return transformResponse(data);
  }

  const config: UseQueryOptions<Project | null> = {
    queryKey: KEYS.project(id),
    queryFn: getProject,
  };

  return useQuery(config);
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  async function createProject(params: {
    name: string;
    description: string;
    domainId: Uuid;
  }): Promise<Project> {
    const response = await api.post<ApiResponse<ProjectServerResponseData>>(
      "/projects",
      params
    );
    const data = response.data.data;

    return transformResponse(data);
  }

  return useMutation({
    mutationFn: createProject,
    onSuccess: (_data: Project, _vars, _context) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
    },
  });
}

export type ProjectUpdateArgs = Pick<
  Project,
  "name" | "description" | "ownerId" | "status"
>;

export function useUpdateProject() {
  const queryClient = useQueryClient();

  async function updateProject(project: Project): Promise<Project> {
    const projectId = project.id;

    const update: ProjectUpdateArgs = pick(
      ["name", "description", "ownerId", "status"],
      project
    );

    const response = await api.patch<ApiResponse<ProjectServerResponseData>>(
      `/projects/${projectId}`,
      update
    );

    const data = response.data.data;

    return transformResponse(data);
  }

  return useMutation({
    mutationFn: updateProject,
    onSuccess: async (data: Project) => {
      // Seed the project detail with the authoritative server response, then
      // invalidate every other project-scoped query. Cancelling in-flight
      // fetches first and excluding the seeded key from invalidation prevents
      // a stale or redundant refetch from clobbering the seeded data.
      const projectKey = KEYS.project(data.id);
      await queryClient.cancelQueries({ queryKey: projectKey });
      queryClient.setQueryData(projectKey, data);
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === SCOPE &&
          query.queryHash !== hashKey(projectKey),
      });
    },
  });
}

export type RevisionUpdateArgs = Pick<Revision, "instructions">;

export function useUpdateRevision() {
  const queryClient = useQueryClient();

  async function updateRevision(params: {
    projectId: Uuid;
    revision: Revision;
  }): Promise<Revision> {
    const update: RevisionUpdateArgs = pick(["instructions"], params.revision);

    const response = await api.patch<ApiResponse<Revision>>(
      `/projects/${params.projectId}/revisions/${params.revision.id}`,
      update
    );

    return response.data.data;
  }

  return useMutation({
    mutationFn: updateRevision,
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: KEYS.project(vars.projectId),
      });
      // The instructions edit also bumps the project's `updatedAt`, which
      // affects the project list's sort order and "Modified" display.
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  async function deleteProject({ id }: { id: Uuid }): Promise<void> {
    await api.delete<ApiResponse<void>>(`/projects/${id}`);
  }

  return useMutation({
    mutationFn: deleteProject,
    onSuccess: (_data, vars: { id: Uuid }, _context) => {
      // Delete before invalidating, to avoid deleted data coming back in the
      // result from refetching invalidated queries.
      queryClient.removeQueries({ queryKey: KEYS.project(vars.id) });
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
    },
  });
}

export function useCreateRevision() {
  const queryClient = useQueryClient();

  async function createRevision(params: { projectId: Uuid }): Promise<Project> {
    const response = await api.post<ApiResponse<ProjectServerResponseData>>(
      `/projects/${params.projectId}/revisions`
    );
    const data = response.data.data;

    return transformResponse(data);
  }

  return useMutation({
    mutationFn: createRevision,
    onSuccess: async (data: Project) => {
      // Seed the project detail with the authoritative server response, then
      // invalidate every other project-scoped query. Cancelling in-flight
      // fetches first and excluding the seeded key from invalidation prevents
      // a stale or redundant refetch from clobbering the seeded data.
      const projectKey = KEYS.project(data.id);
      await queryClient.cancelQueries({ queryKey: projectKey });
      queryClient.setQueryData(projectKey, data);
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === SCOPE &&
          query.queryHash !== hashKey(projectKey),
      });
    },
  });
}

async function getRevisionResources(projectId: Uuid, revisionId: Uuid) {
  const response = await api.get<ApiResponse<BlobRecord[]>>(
    `/projects/${projectId}/revisions/${revisionId}/resources`
  );
  return response.data.data;
}

export function useRevisionResources(revision: Revision | undefined) {
  const projectId = revision?.projectId;
  const revisionId = revision?.id;

  return useQuery({
    queryKey: KEYS.revisionResources(projectId, revisionId),
    queryFn:
      projectId && revisionId
        ? () => getRevisionResources(projectId, revisionId)
        : skipToken,
  });
}

/**
 * For an optimization, find the revision (if any) that was created with this
 * optimization's optimized G-code. Note that there may be later revisions that
 * share the same G-code, so this hook returns the first revision that matches.
 *
 * We may want to add a backend endpoint to get this information without
 * iterating.
 */
export function useOptimizationCreatedRevision(
  project: Project | undefined,
  optimization: Optimization | undefined
) {
  const projectId = project?.id;
  const revisionId = optimization?.revisionId;
  const optimizationId = optimization?.job.id;
  const optimizedGcodeId = optimization?.optimizedGcode?.id;
  const revisions = project?.revisions;

  async function getOptimizationCreatedRevision() {
    // can happen before the project is loaded
    if (!revisions || !projectId) return undefined;

    // Iterate through later revisions until we either find one with the same ID
    // as the optimization's optimized G-code, or we run out of revisions. Note
    // that we iterate instead of running in parallel because the new revision is
    // almost always the next one, and we don't want to make unnecessary server
    // requests.
    const startIndex = revisions.findIndex((r) => r.id === revisionId);
    if (startIndex === -1) {
      throw new Error("Revision not found in project");
    }

    for (let i = startIndex + 1; i < revisions.length; i++) {
      const revision = revisions[i];
      const resources = await getRevisionResources(projectId, revision.id);
      if (resources.some((r) => r.id === optimizedGcodeId)) {
        return revision;
      }
    }
    return null; // No matching revision found
  }

  return useQuery({
    queryKey: KEYS.optimizationCreatedRevisions(projectId, optimizationId),
    queryFn:
      projectId && revisionId && optimizationId
        ? getOptimizationCreatedRevision
        : skipToken,
  });
}

export const useRevisionMaterials = (revision: Revision) => {
  const { data: materials } = useMaterials();

  const mat0 = materials?.find((m) => m.id === revision.materials?.[0]);
  const mat1 = materials?.find((m) => m.id === revision.materials?.[1]);

  const canOptimize =
    mat0?.capabilities.optimization &&
    (!mat1 || mat1?.capabilities.optimization);

  return { mat0, mat1, canOptimize };
};

export const sortAttempts = (a: Attempt, b: Attempt) => {
  return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
};

// Helper function to get a starting timestamp for either an Attempt or
// Optimization record, in order to be able to sort them together.
function getTimestampForAttemptOrOptimization(
  item: Attempt | Optimization
): number {
  if ("startedAt" in item) {
    // Attempt
    return new Date(item.startedAt).getTime();
  } else if ("job" in item && item.job.createdAt) {
    // Optimization
    return new Date(item.job.createdAt).getTime();
  }
  throw new Error("Unknown item type; cannot determine timestamp.");
}

export const sortAttemptsAndOptimizations = (
  a: Attempt | Optimization,
  b: Attempt | Optimization
) => {
  return (
    getTimestampForAttemptOrOptimization(b) -
    getTimestampForAttemptOrOptimization(a)
  );
};

type AttemptWithoutNumber = Omit<Attempt, "number">;

export function useProjectAttempts(projectId: Uuid) {
  async function getAttempts(projectIdInner: Uuid): Promise<Attempt[]> {
    const response = await api.get<ApiResponse<AttemptWithoutNumber[]>>(
      `/projects/${projectIdInner}/attempts`
    );
    return appendNumberProperty(response.data.data);
  }

  const config: UseQueryOptions<Attempt[]> = {
    staleTime: 1000 * 60, // detect status changes after 1 minute
    queryKey: KEYS.projectAttempts(projectId),
    queryFn: () => {
      return getAttempts(projectId);
    },
  };

  return useQuery(config);
}

type OptimizationWithoutNumber = Omit<Optimization, "number">;

/**
 * Return an array of Attempt or Optimization objects with a `number` property
 * attached. This property is a 1-based index, grouped by revisionId. The oldest
 * attempt or optimization for a given revision will have `number: 1`. This
 * function assumes that the input data is already sorted in descending order
 * from the index order.
 *
 * At some point soon we'll probably want to move this logic to the server, and
 * then we can remove this function.
 * */
function appendNumberProperty<T extends { revisionId: Uuid }>(
  data: T[]
): (T & { number: number })[] {
  const grouped = groupById(data, "revisionId");
  return data.map((item) => {
    const itemsForNumbering = grouped[item.revisionId];
    const number = itemsForNumbering.length - itemsForNumbering.indexOf(item);
    return { ...item, number };
  });
}

export function useProjectOptimizations(
  projectId: Uuid,
  { parentOnly, parentId }: { parentOnly?: boolean; parentId?: Uuid } = {}
) {
  async function getOptimizations(
    projectIdInner: Uuid
  ): Promise<Optimization[]> {
    const response = await api.get<ApiResponse<OptimizationWithoutNumber[]>>(
      `/projects/${projectIdInner}/optimizations`
    );
    return appendNumberProperty(response.data.data);
  }

  const query = useQuery<Optimization[]>({
    placeholderData: [],
    queryKey: KEYS.projectOptimizations(projectId),
    queryFn: () => getOptimizations(projectId),
    // Poll every 5s while any job is in a non-terminal state, so the UI stays up to date.
    refetchInterval: (queryInner) =>
      queryInner.state.data?.some((o) =>
        ["queued", "pending", "started"].includes(o.job.status)
      )
        ? 5000
        : false,
  });

  const data = parentOnly
    ? query.data?.filter((o) => !o.job.input.parentOptimizationId)
    : parentId !== undefined
      ? query.data?.filter((o) => o.job.input.parentOptimizationId === parentId)
      : query.data;

  return { ...query, data };
}

const useAllAttemptsOmittedOptions = [
  "queryKey",
  "queryFn",
  "refetchOnWindowFocus",
  "refetchInterval",
] as const;

/**
 * Get all attempts for all projects, for use in the home page dashboard.
 */
export function useAllAttempts<T = Attempt[]>(
  options: Omit<
    UseQueryOptions<Attempt[], unknown, T, string[]>,
    (typeof useAllAttemptsOmittedOptions)[number]
  > = {}
) {
  async function getAttempts(): Promise<Attempt[]> {
    const response =
      await api.get<ApiResponse<AttemptWithoutNumber[]>>(`/attempts`);
    return appendNumberProperty(response.data.data);
  }

  for (const option of useAllAttemptsOmittedOptions) {
    // Cast needed to validate the input doesn't match the expected type.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    if ((options as UseQueryOptions)[option] !== undefined) {
      throw new Error(`Invalid option: ${option}`);
    }
  }

  return useQuery({
    ...options,
    queryKey: KEYS.allAttempts(),
    queryFn: getAttempts,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get all optimizations for all projects, for use in the home page dashboard.
 */
export function useAllOptimizations() {
  async function getOptimizations(): Promise<Optimization[]> {
    const response =
      await api.get<ApiResponse<OptimizationWithoutNumber[]>>(`/optimizations`);
    return appendNumberProperty(response.data.data);
  }

  return useQuery({
    staleTime: 1000 * 60,
    queryKey: KEYS.allOptimizations(),
    queryFn: getOptimizations,
    refetchOnWindowFocus: false,
  });
}

type UseAttemptDatasetManifestData = {
  /** The datasets available on the server for this attempt */
  manifest: AttemptDatasetManifest[] | undefined;
  /**
   * Will be `true` if the attempt has started recently so we're still waiting
   * to see if more datasets will show up on the server.
   * */
  isPolling: boolean;
  /** Does the manifest have enough datasets to show in the Analyze page? */
  isMinimalViewableManifest: boolean;
};

/**
 * Get the dataset manifest for an attempt. The manifest lists which sensor
 * datasets are available for the attempt, which is necessary to know before we
 * can show the Analyze page.
 *
 * Note that the manifest is populated incrementally on the server as new data
 * comes in. This means that a manifest right at the start of a print may
 * include fewer datasets than it will later. Therefore, if this hook is called
 * right after the start of a print job, we'll poll for a short period to allow
 * all datasets to be populated in the manifest before we conclude that there is
 * no data to show. The `isPolling` return value indicates whether the hook is
 * currently polling for the manifest to be populated, which can be used by the
 * caller to decide whether to show a loading spinner or a "no data available"
 * message.
 *
 * The `isMinimalViewableManifest` return value indicates whether the manifest
 * is fully loaded and ready to be shown. This can be used by the caller to
 * decide whether to show a "data is still loading" message versus showing the
 * actual Analyze page content.
 */
export function useAttemptDatasetManifest(
  { projectId, attempt }: { projectId: Uuid; attempt: Attempt | undefined },
  config?: Omit<
    UseQueryOptions<
      AttemptDatasetManifest[] | undefined,
      Error,
      UseAttemptDatasetManifestData,
      ReturnType<typeof KEYS.attempt>
    >,
    "queryFn" | "queryKey"
  >
) {
  async function getAttemptDatasetManifest(
    projectIdInner: Uuid,
    attemptId: Uuid
  ) {
    const response = await api.get<ApiResponse<AttemptDatasetManifest[]>>(
      `/projects/${projectIdInner}/attempts/${attemptId}/data`
    );
    const data = response.data.data;

    return data;
  }

  return useQuery({
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    enabled: true,
    queryKey: KEYS.attempt(projectId, attempt?.id),
    queryFn: attempt
      ? () => getAttemptDatasetManifest(projectId, attempt.id)
      : skipToken,
    refetchInterval: (query) => {
      if (!attempt) return false;
      const manifest = query.state.data;

      // If the attempt started recently, there may be a partial manifest
      // because each dataset is created independently and may need a few
      // seconds to become available in the manifest. If there's a partial
      // manifest, then poll a few times before giving up and assuming that the
      // data just isn't viewable.
      return isPollingForManifest(attempt, manifest)
        ? POLL_INTERVAL_WHILE_WAITING
        : false;
    },
    select: (data): UseAttemptDatasetManifestData => {
      const result = {
        isPolling: isPollingForManifest(attempt, data),
        isMinimalViewableManifest: !!data && !!isViewableAttemptData(data),
        manifest: data,
      };
      return result;
    },
    ...config,
  });
}

const MSECS_TO_WAIT_AFTER_START = 120 * 1000;
const POLL_INTERVAL_WHILE_WAITING = 2 * 1000;

function isPollingForManifest(
  attempt: Attempt | undefined,
  data: AttemptDatasetManifest[] | undefined
) {
  if (!attempt) return false;
  return (
    isViewableAttemptData(data) !== "all" &&
    Temporal.Instant.from(attempt.startedAt)
      .until(Temporal.Now.instant())
      .total("milliseconds") < MSECS_TO_WAIT_AFTER_START
  );
}

/**
 * Refresh an in-progress attempt periodically according to the caller's criteria.
 *
 * This is not practical constantly during a print because we don't have a way
 * to incrementally fetch new attempt data from the server. We'd swamp the
 * user's network connection re-downloading fresh data each time.
 *
 * But reloading just the attempt record itself is cheap, and is useful in cases
 * like these:
 * - When a print finishes or pauses, we'll want to auto-update the UI.
 * - When a print has completed, we'll want to see if the timelapse is ready.
 *
 * Callers must provide `options.refetchInterval` to control periodic polling.
 * Query options control initial fetching and window-focus refresh independently.
 */
export function usePolledAttempt(
  project: Project,
  attempt: Attempt | undefined,
  options: Omit<UseQueryOptions<Attempt | undefined>, "queryKey" | "queryFn"> &
    Required<Pick<UseQueryOptions<Attempt | undefined>, "refetchInterval">>
) {
  const config: UseQueryOptions<Attempt | undefined> = {
    queryKey: KEYS.polledAttempt(project.id, attempt?.id),
    queryFn: attempt
      ? async () => {
          const response = await api.get<ApiResponse<Attempt>>(
            `/projects/${project.id}/attempts/${attempt.id}`
          );
          return response.data.data;
        }
      : skipToken,
    staleTime: 0,
    ...options,
    enabled: (query) =>
      attempt !== undefined &&
      (typeof options.enabled === "function"
        ? options.enabled(query)
        : (options.enabled ?? true)),
  };

  return useQuery(config);
}

/**
 * Returns the most recent attempt for each machine, as an object with the key
 * being the machine ID and the value being the `Attempt`. If a machine has no
 * attempts then it will not be included in the object.
 */
export function useLastAttemptForEachMachine() {
  return useAllAttempts({
    select: (data) => {
      // There's no need to sort the data because the server returns it sorted
      // with the most-recently-started attempts first.
      const grouped = groupById(data, "machineId");
      const result = Object.fromEntries(
        Object.entries(grouped).map(([machineId, attempts]) => [
          machineId,
          attempts[0],
        ])
      );

      return result;
    },
  });
}

export function useMachineAttempts(machine: Machine | undefined) {
  return useAllAttempts({
    enabled: machine !== undefined,
    select: (data) => {
      return machine
        ? data.filter((attempt) => attempt.machineId === machine.id)
        : undefined;
    },
  });
}

// Function to handle the multipart POST request to create a new project revision
const reviseProject = async (params: {
  projectId: string;
  revisionChange: RevisionChange;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  onSuccess?: (data: Revision) => void;
}) => {
  const { projectId, revisionChange, onUploadProgress } = params;

  const formData = new FormData();

  if (revisionChange.gcode) {
    const gcodeData =
      revisionChange.gcode instanceof File
        ? revisionChange.gcode
        : revisionChange.gcode.id;

    formData.append("gcode", gcodeData);
  }

  if (revisionChange.models) {
    revisionChange.models.forEach((model) => {
      const modelData = model instanceof File ? model : model.id;
      formData.append(`models`, modelData);
    });
  }

  if (revisionChange.materials) {
    formData.append(
      "materials",
      JSON.stringify([
        revisionChange.materials.material1Id,
        revisionChange.materials.material2Id,
      ])
    );
  }
  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress,
  };

  return await api.post<ApiResponse<Revision>>(
    `/projects/${projectId}/revisions`,
    formData,
    config
  );
};

export function useReviseProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reviseProject,
    onSuccess: async (data, variables) => {
      const newRevision = data.data.data;

      // Await the refetch so the cached project includes the new revision
      // before the caller's onSuccess runs. invalidateQueries already refetches
      // the active project query and resolves once it settles.
      await queryClient.invalidateQueries({
        queryKey: KEYS.project(newRevision.projectId),
      });

      variables.onSuccess?.(newRevision);
    },
  });
}

/**
 * Helper function to determine whether an attempt's datasets will be usable by
 * the viewer.
 * - `"all"` means the manifest includes all datasets needed for the full viewer
 *   experience.
 * - `"minimal"` means the manifest includes a minimal set of datasets that
 *   allows us to show at least some content in the viewer, even if it's not the
 *   full experience.
 * - `false` means the manifest doesn't include enough datasets to show anything
 *   in the viewer.
 */
function isViewableAttemptData(
  response: AttemptDatasetManifest[] | undefined
): "all" | "minimal" | false {
  if (!response) {
    return false;
  }

  const kindsWithSamples = response
    .filter((item) => item.samples > 0)
    .map((item) => item.kind);

  const hasMotionData = kindsWithSamples.includes("motionData");
  const hasToolForce0Data = kindsWithSamples.includes("toolForce0");
  const hasToolForce1Data = kindsWithSamples.includes("toolForce1");
  const hasEncoderData = kindsWithSamples.includes("axesEncoders");
  const hasLabData = kindsWithSamples.some((kind) =>
    /^lab:[A-Za-z0-9_]{1,64}$/.test(kind)
  );

  if (hasMotionData && hasToolForce0Data && hasToolForce1Data) {
    return "all";
  }

  if (
    hasMotionData &&
    (hasToolForce0Data || hasToolForce1Data || hasEncoderData || hasLabData)
  ) {
    return "minimal";
  }

  return false;
}

export function useAttemptData(
  projectId: Uuid,
  attemptId: Uuid,
  datasetId: Uuid,
  setProgress?: (progress: number) => void,
  options: Omit<
    UseQueryOptions<ArrayBuffer | undefined>,
    "queryKey" | "queryFn"
  > = {}
) {
  const pathFunc = (id: Uuid) => {
    return `/projects/${projectId}/attempts/${attemptId}/data/${id}`;
  };

  const config: UseQueryOptions<ArrayBuffer | undefined> = {
    queryKey: KEYS.attemptData(projectId, attemptId, datasetId),
    queryFn: async ({ signal }) => {
      const blobRecordResponse = await api.get<ApiResponse<BlobRecord>>(
        `/blobs/${datasetId}`
      );
      const blobRecord = blobRecordResponse.data.data;
      return await fetchBlobContentsParallel(
        blobRecord,
        pathFunc,
        setProgress,
        signal
      );
    },
    staleTime: Infinity,
    ...options,
  };

  return useQuery(config);
}

export type SimSettings = {
  optimizeGcode: boolean;
};

// Create an optimization record for a revision
async function createOptimizationRecord(
  revision: Revision,
  settings: SimSettings
): Promise<OptimizationWithoutNumber> {
  const response = await api.post<ApiResponse<OptimizationWithoutNumber>>(
    `/projects/${revision.projectId}/revisions/${revision.id}/optimizations`,
    settings
  );
  return response.data.data;
}

// Custom hook to create an optimization
export function useCreateOptimization(revision: Revision) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: SimSettings) =>
      await createOptimizationRecord(revision, settings),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: KEYS.allOptimizations() }),
  });
}

async function startOptimizationComparison(params: {
  projectId: Uuid;
  revisionId: Uuid;
  optimizationId: Uuid;
}): Promise<void> {
  await api.post<ApiResponse<void>>(
    `/projects/${params.projectId}/revisions/${params.revisionId}/optimizations/${params.optimizationId}/comparison`
  );
}

export function useStartOptimizationComparison(params: {
  projectId: Uuid;
  revisionId: Uuid;
  optimizationId: Uuid;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => startOptimizationComparison(params),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: KEYS.projectOptimizations(params.projectId),
      }),
  });
}
